import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

// Components
import { SearchableList } from '../SearchableList';
import { MaterialIcon } from '../MaterialIcon';

const DualListContainer = styled.div`
    display: flex;
`;

const DualListItem = styled.div`
    flex: 50%;
    width: 50%;
    padding: 8px 8px 0 8px;
`;

const DualListTitle = styled.h4`
    margin-top: 0;
    margin-bottom: 1rem;
`;

const DualListRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    opacity: ${({ muted }) => (muted ? 0.3 : 1)};
`;

export const DualListRowCode = styled.div`
    min-width: 5rem;
`;

export const DualListRowName = styled.span`
    flex: 1;
    margin-left: 1rem;
    span {
        color: ${({ theme }) => theme.colors.fixed.white};
        font-size: 0.8rem;
        font-weight: bold;
    }
`;

const DualListRowIcon = styled.div`
    display: flex;
    min-width: 1.5rem;
    flex-direction: row-reverse;
`;

export const DualList = ({
    allItems = [],
    selected = {},
    onSelect = () => {},
    isLoading = false,
    renderRowContents = () => {},
    primaryKey,
    contentType,
    searchableProperties = []
}) => {
    const renderRow = (item, isSelected) => {
        return <DualListRow muted={isSelected}>{renderRowContents(item)}</DualListRow>;
    };
    const renderIcon = (icon) => (
        <DualListRowIcon>
            <MaterialIcon icon={icon} intent={`muted`} />
        </DualListRowIcon>
    );
    const searchFilterFn = React.useCallback(
        ({ items = [], searchFilter = '' }) => {
            if (items.length && searchFilter) {
                return items.filter((item) =>
                    searchableProperties
                        .map((prop) => item?.[prop])
                        .some((str = '') => {
                            const lowerStr = str.toLowerCase();

                            return lowerStr.includes(searchFilter.toLowerCase());
                        })
                );
            } else {
                return items ?? [];
            }
        },
        [searchableProperties]
    );

    return (
        <DualListContainer>
            <DualListItem>
                <DualListTitle>All {contentType}</DualListTitle>
                <SearchableList
                    id={`all-${contentType}`}
                    items={allItems}
                    loading={isLoading}
                    onItemClick={onSelect}
                    itemKey={primaryKey}
                    renderRow={(item) => {
                        const isSelected = selected[item?.[primaryKey]];

                        return renderRow(item, isSelected);
                    }}
                    renderIcon={(item) => renderIcon(selected[item.codeId] ? 'done' : null)}
                    listHeight={'50vh'}
                    emptyStateLabel={contentType}
                    filterFn={searchFilterFn}
                />
            </DualListItem>
            <DualListItem>
                <DualListTitle>Applied {contentType}</DualListTitle>
                <SearchableList
                    id={`selected-${contentType}`}
                    loading={isLoading}
                    items={allItems.filter((item) => selected[item?.[primaryKey]])}
                    onItemClick={onSelect}
                    itemKey={'codeId'}
                    listHeight={'calc(50vh - 2rem)'}
                    renderRow={renderRow}
                    renderIcon={(item) => renderIcon('remove')}
                    emptyStateLabel={`selected ${contentType}`}
                    filterFn={searchFilterFn}
                    renderResultsCount={(count) => `${count} selected`}
                />
            </DualListItem>
        </DualListContainer>
    );
};

export default DualList;

DualList.propTypes = {
    allItems: PropTypes.arrayOf(PropTypes.object),
    selected: PropTypes.object,
    onSelect: PropTypes.func,
    isLoading: PropTypes.bool,
    renderRowContents: PropTypes.func,
    primaryKey: PropTypes.string,
    contentType: PropTypes.oneOf(['codes', 'concepts']),
    searchableProperties: PropTypes.arrayOf(PropTypes.string)
};
